import { ReactElement } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { MenuItem, Select } from '@mui/material'
import type { GridRenderEditCellParams } from '@mui/x-data-grid-premium'
import type { ProjectMemberDTO } from '@api/fastAPI.schemas'
import { useGetProjectMembers } from '@api/project-members/project-members'
import { useSnackbar } from '@ui/snackbar'

interface Props extends GridRenderEditCellParams<{ project: ProjectData }> {
  onReassign: (project: ProjectData, primaryEditorGuid: string | null) => void
}

const UNASSIGNED = 'unassigned'

/**
 * The Primärbearbeiter picker, rendered by DataGrid's own edit-mode lifecycle
 * (Escape/click-outside commit, keyboard nav, mutual exclusivity with other
 * cells' edit mode all come from that for free).
 *
 * The candidates are the members of the project's own organisation, fetched
 * only once editing starts -- a list per row would be one request per project
 * for something most people never touch.
 */
const PrimaryEditorEditCell = ({ id, field, api, row, onReassign }: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const project = row.project

  const { data: candidates } = useGetProjectMembers<ProjectMemberDTO[], AxiosError>(project.guid, {
    query: {
      retry: false,
      select: data => data.members,
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Bearbeiter'), {
          variant: 'error',
        })
      },
    },
  })

  const stopEditing = () => api.stopCellEditMode({ id, field })

  return (
    <Select
      size="small"
      autoFocus
      open
      value={project.primary_editor_guid || UNASSIGNED}
      onClose={stopEditing}
      onChange={event => {
        const value = event.target.value
        onReassign(project, value === UNASSIGNED ? null : value)
        stopEditing()
      }}
      data-cy="select-primary-editor"
      sx={{ width: '100%' }}
    >
      {/* A project without a Primärbearbeiter is a real state, not a gap. */}
      <MenuItem value={UNASSIGNED}>—</MenuItem>
      {(candidates || []).map(candidate => (
        <MenuItem key={candidate.guid} value={candidate.guid}>
          {candidate.name || candidate.email || candidate.guid}
        </MenuItem>
      ))}
    </Select>
  )
}

export default PrimaryEditorEditCell
